/**
 * Connecting read-only calendars: an Outlook published feed, a Google secret
 * iCal address, anything serving ICS over https.
 *
 * The URL is a credential — a published calendar has no sign-in behind it —
 * so the server never sends it back. That shapes this component: a saved feed
 * shows a masked hint and an empty URL box, and submitting with that box empty
 * means "leave the stored one alone" rather than "clear it".
 *
 * Testing before saving is a button of its own because pasting the wrong link
 * is the likeliest way this goes wrong, and discovering it at save time — with
 * the wrong URL already stored — is a worse place to find out.
 */

import { useEffect, useState } from 'react'

import { ApiError, api } from '@/api/client'
import type { CalendarFeed, CalendarTestResult } from '@/api/types'
import { useToast } from '@/components/Toast'
import { CATEGORY_PALETTE, readableTextOn } from '@/lib/color'
import { formatTimestamp } from '@/lib/format'

/**
 * The per-calendar display filter.
 *
 * A textarea of one phrase per line rather than chips: the phrases contain
 * spaces ("Josh Dentist"), so a space cannot be the separator, and a line
 * break is the one delimiter nobody has to be told about.
 *
 * Saves on blur, like every other inline edit in the app. The count underneath
 * is the point of the whole control -- a phrase that matches nothing hides the
 * calendar completely, and without a number saying "0 of 42" that looks
 * identical to an empty calendar.
 */
function KeywordFilter({
  feed,
  onSave,
}: {
  feed: CalendarFeed
  onSave: (keywords: string[]) => Promise<void>
}) {
  const [text, setText] = useState(feed.keywords.join('\n'))
  const [open, setOpen] = useState(feed.keywords.length > 0)

  // Re-sync when the server normalises what was typed (trimming, de-duping),
  // so the box shows what is actually stored rather than what was sent.
  useEffect(() => {
    setText(feed.keywords.join('\n'))
  }, [feed.keywords])

  const filtering = feed.keywords.length > 0
  const matched = feed.visible_event_count

  if (!open) {
    return (
      <button
        type="button"
        className="self-start text-xs font-medium text-navy underline underline-offset-2"
        onClick={() => setOpen(true)}
      >
        Show only certain events…
      </button>
    )
  }

  return (
    <div className="mt-1 w-full">
      <label className="text-xs font-semibold text-ink-2" htmlFor={`kw-${feed.id}`}>
        Show only events containing
      </label>
      <textarea
        id={`kw-${feed.id}`}
        className="field mt-1 text-xs"
        rows={Math.min(Math.max(feed.keywords.length + 1, 2), 6)}
        placeholder={'Josh Dentist\nJosh Ortho'}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onBlur={() => {
          const next = text
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
          // Nothing changed, so do not spend a request saying so.
          if (next.join('\n') === feed.keywords.join('\n')) return
          void onSave(next)
        }}
        spellCheck={false}
      />
      <p className="mt-1 text-xs text-ink-3">
        One exact phrase per line, any of which is enough. Capitalisation is ignored.{' '}
        {filtering ? (
          <span className={matched === 0 ? 'font-semibold text-crimson' : ''}>
            {matched === 0
              ? 'No cached events match — nothing from this calendar will show.'
              : `${matched} of ${feed.last_event_count} cached events match.`}
          </span>
        ) : (
          <span>Empty shows everything from this calendar.</span>
        )}
      </p>
    </div>
  )
}

function StatusLine({ feed }: { feed: CalendarFeed }) {
  if (feed.last_status === 'error') {
    return (
      <span className="text-xs text-crimson" title={feed.last_error ?? undefined}>
        {feed.last_error ?? 'Last refresh failed.'}
      </span>
    )
  }
  if (!feed.last_fetched_at) {
    return <span className="text-xs text-ink-3">Not fetched yet.</span>
  }
  return (
    <span className="text-xs text-ink-3">
      {feed.last_event_count} event{feed.last_event_count === 1 ? '' : 's'} · refreshed{' '}
      {formatTimestamp(feed.last_fetched_at)}
    </span>
  )
}

export function CalendarFeeds() {
  const toast = useToast()
  const [feeds, setFeeds] = useState<CalendarFeed[]>([])
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [color, setColor] = useState<string>(CATEGORY_PALETTE[0])
  const [busy, setBusy] = useState(false)
  const [tested, setTested] = useState<CalendarTestResult | null>(null)

  useEffect(() => {
    api.calendars
      .list()
      .then(setFeeds)
      .catch(() => toast.error('Could not load connected calendars.'))
      .finally(() => setLoading(false))
  }, [toast])

  function fail(caught: unknown, fallback: string) {
    toast.error(caught instanceof ApiError ? caught.message : fallback)
  }

  async function test() {
    if (!url.trim()) return
    setBusy(true)
    setTested(null)
    try {
      setTested(await api.calendars.test({ name: name.trim() || 'Calendar', url: url.trim() }))
    } catch (caught) {
      fail(caught, 'Could not reach that calendar.')
    } finally {
      setBusy(false)
    }
  }

  async function add() {
    if (!name.trim() || !url.trim()) {
      toast.error('A calendar needs a name and a URL.')
      return
    }
    setBusy(true)
    try {
      const created = await api.calendars.create({ name: name.trim(), url: url.trim(), color })
      setFeeds((current) => [...current, created])
      setName('')
      setUrl('')
      setTested(null)
      toast.success(
        created.last_status === 'ok'
          ? `Connected ${created.name}: ${created.last_event_count} events.`
          : `Connected ${created.name}, but the first refresh failed.`,
      )
    } catch (caught) {
      fail(caught, 'Could not connect that calendar.')
    } finally {
      setBusy(false)
    }
  }

  async function patch(feed: CalendarFeed, changes: Parameters<typeof api.calendars.update>[1]) {
    try {
      const updated = await api.calendars.update(feed.id, changes)
      setFeeds((current) => current.map((f) => (f.id === feed.id ? updated : f)))
    } catch (caught) {
      fail(caught, 'Could not save that change.')
    }
  }

  async function refresh(feed: CalendarFeed) {
    try {
      const updated = await api.calendars.refresh(feed.id)
      setFeeds((current) => current.map((f) => (f.id === feed.id ? updated : f)))
      toast.success(
        updated.last_status === 'ok'
          ? `${updated.name}: ${updated.last_event_count} events.`
          : `${updated.name} could not be refreshed.`,
      )
    } catch (caught) {
      fail(caught, 'Could not refresh that calendar.')
    }
  }

  async function remove(feed: CalendarFeed) {
    try {
      await api.calendars.remove(feed.id)
      setFeeds((current) => current.filter((f) => f.id !== feed.id))
      toast.success(`Disconnected ${feed.name}.`)
    } catch (caught) {
      fail(caught, 'Could not disconnect that calendar.')
    }
  }

  if (loading) return <p className="text-sm text-ink-3">Loading…</p>

  return (
    <div className="flex flex-col gap-3">
      {feeds.map((feed) => (
        <div key={feed.id} className="flex flex-wrap items-center gap-2 rounded-md border border-line p-2">
          <span
            aria-hidden
            className="size-3 shrink-0 rounded-full"
            style={{ backgroundColor: feed.color ?? '#004077' }}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium">{feed.name}</span>
              {!feed.is_enabled && <span className="pill bg-surface-3 text-ink-3">off</span>}
            </div>
            <div className="truncate text-xs text-ink-3" title={feed.url_hint}>
              {feed.url_hint}
            </div>
            <StatusLine feed={feed} />
          </div>

          <label className="flex items-center gap-1 text-xs text-ink-2">
            <input
              type="checkbox"
              checked={feed.is_enabled}
              onChange={(event) => void patch(feed, { is_enabled: event.target.checked })}
            />
            Show
          </label>
          <button type="button" className="btn btn-neutral btn-sm" onClick={() => void refresh(feed)}>
            Refresh
          </button>
          <button type="button" className="btn btn-danger btn-sm" onClick={() => void remove(feed)}>
            Remove
          </button>

          <KeywordFilter feed={feed} onSave={(keywords) => patch(feed, { keywords })} />
        </div>
      ))}

      <div className="flex flex-col gap-2 rounded-md border border-dashed border-line-strong p-3">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_2fr]">
          <label className="text-xs font-semibold text-ink-2">
            Name
            <input
              className="field mt-1"
              placeholder="BYU"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={100}
            />
          </label>
          <label className="text-xs font-semibold text-ink-2">
            Published ICS URL
            <input
              className="field mt-1"
              placeholder="https://outlook.office365.com/owa/calendar/…/calendar.ics"
              value={url}
              onChange={(event) => {
                setUrl(event.target.value)
                setTested(null)
              }}
              // Not type="url": that adds a browser validation bubble on a
              // field where our own test button gives a far better message.
              autoComplete="off"
              spellCheck={false}
            />
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-semibold text-ink-2">Colour</span>
          {CATEGORY_PALETTE.map((swatch) => (
            <button
              key={swatch}
              type="button"
              aria-label={`Use ${swatch}`}
              aria-pressed={color === swatch}
              onClick={() => setColor(swatch)}
              className="size-6 rounded-full border-2 transition-transform"
              style={{
                backgroundColor: swatch,
                borderColor: color === swatch ? readableTextOn(swatch) : 'transparent',
                transform: color === swatch ? 'scale(1.15)' : undefined,
              }}
            />
          ))}
        </div>

        {tested && (
          <p
            role="status"
            className={`rounded-md px-3 py-2 text-sm ${
              tested.ok ? 'bg-success-light text-success-text' : 'bg-error-light text-error-text'
            }`}
          >
            {tested.message}
            {tested.next_event && <span className="block text-xs">Next: {tested.next_event}</span>}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn btn-neutral btn-sm" onClick={() => void test()} disabled={busy || !url.trim()}>
            {busy ? 'Checking…' : 'Test'}
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => void add()} disabled={busy}>
            Connect
          </button>
        </div>
      </div>
    </div>
  )
}
